import React, { forwardRef } from 'react';

/**
 * Single search suggestion item.
 *
 * @param {object} item - The search result item with title, url, id.
 * @param {number} total - Total number of results.
 * @param {number} index - Index of this item.
 * @param {string} term - Search term to highlight.
 * @param {int} searchBoxIndex - The index of the searchbox.
 * @param {string} optionId - Unique ID for this option.
 * @param {boolean} isActive - Whether this option is currently active.
 * @param {function} onKeyDown - Keyboard handler for this item.
 * @return {JSX.Element}
 * @constructor
 */
const SearchSuggestionsItem = ({item: {title, url, id}, total, index, term, searchBoxIndex = 0, optionId, isActive, onKeyDown}) => {
  const {
    svg_sprite: _SPRITE
  } = window.wp_variables;

  return (
    <li
      id={optionId}
      key={`${searchBoxIndex}-${id}-${index}`}
      className="denhaag-link-group__list-item"
      role="option"
      aria-selected={isActive}
    >
      <a
        aria-posinset={index + 1}
        aria-setsize={total}
        className="denhaag-link denhaag-link--with-icon denhaag-link--with-icon-start"
        href={url}
        target="_self"
        onKeyDown={onKeyDown ? (e) => onKeyDown(e, index) : undefined}
      >
        <svg className="denhaag-icon" aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <use href={`${_SPRITE}#nlds-icon-arrow-right`} />
        </svg>
        <span className='sr-only'>{title}</span>
        <span aria-hidden="true" className="denhaag-link__label"
              dangerouslySetInnerHTML={{__html: SuggestionLabel(title, term.toLowerCase())}}>
        </span>
      </a>
    </li>
  );
};

/**
 * List of search results.
 * @param {array} items Array of objects.
 * @param {string} term The search term.
 * @param {string} ariaID ID of the aria element.
 * @param {int} searchBoxIndex The index of the searchbox.
 * @param {number} activeIndex The currently active index.
 * @param {function} onItemKeyDown Keyboard handler for items.
 * @param {object} ref Forwarded ref.
 * @return {JSX.Element}
 * @constructor
 */
const SearchSuggestions = forwardRef(({items = [], term, ariaID, searchBoxIndex = 0, activeIndex = -1, onItemKeyDown}, ref) => {
  const {
    search_endpoint_type: _SEARCH_TYPE,
  } = window.wp_variables;

  if ('relevanssi' === _SEARCH_TYPE) {
    // Results when it's relevanssi.
    return (
      <ul ref={ref} id={ariaID} role="listbox" className="utrecht-link-list utrecht-link-list--html-ul denhaag-link-group__list">
        {items && items.map(({title: {rendered: title}, link: url, id, key}, index) => (
          <SearchSuggestionsItem
            item={{title, url, id}}
            total={items.length}
            index={index}
            term={term}
            searchBoxIndex={searchBoxIndex}
            key={key || `${searchBoxIndex}-${id}-${index}`}
            optionId={`${ariaID}-option-${index}`}
            isActive={index === activeIndex}
            onKeyDown={onItemKeyDown}
          />
        ))}
      </ul>
    );
  }


  // Search results when using the default WP search endpoint.
  return (
    <ul ref={ref} id={ariaID} role="listbox" className="utrecht-link-list utrecht-link-list--html-ul denhaag-link-group__list">
      {items && items.map(({title, url, id}, index) => (
        <SearchSuggestionsItem
          item={{title, url, id}}
          total={items.length}
          index={index}
          term={term}
          searchBoxIndex={searchBoxIndex}
          key={`${searchBoxIndex}-${id}-${index}`}
          optionId={`${ariaID}-option-${index}`}
          isActive={index === activeIndex}
          onKeyDown={onItemKeyDown}
        />
      ))}
    </ul>
  );
});

SearchSuggestions.displayName = 'SearchSuggestions';

/**
 * Check suggestion label and set bold when searchterm is in it.
 *
 * @param {string} label, Whole string to search in.
 * @param {string} term, Searchterm to search for.
 * @return {*|ReturnType<typeof domToReact>}
 * @constructor
 */
const SuggestionLabel = (label, term) => {
  // Return label with overwrites.
  if (label && term && label.toLowerCase().includes(term)) {
    const regex = new RegExp(term, 'gi');
    return label.replace(regex, match => `<strong>${match}</strong>`);
  }

  // Return default label.
  return label;
};

export default SearchSuggestions;
